import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const data = await login(email, password);
      const role = data.user.role;
      if (role === "super_admin") navigate("/admin", { replace: true });
      else if (role === "tenant_admin") navigate("/studio", { replace: true });
      else if (role === "subscriber" && data.tenant_slug)
        navigate(`/s/${data.tenant_slug}/select-profile`, { replace: true });
      else navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#0a0a0a] text-white" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-white/5 relative overflow-hidden grain">
        <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs uppercase tracking-[0.3em]" data-testid="login-back-home">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Vawworld
        </Link>
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Vawworld OTT</p>
          <h1 className="mt-4 font-display font-black tracking-tighter text-5xl xl:text-6xl leading-[0.95]">
            Step inside the<br />
            <span className="italic font-medium">control room.</span>
          </h1>
          <p className="mt-6 text-white/55 font-light max-w-md">
            One sign-in for super admins, studio operators and subscribers. We'll route you to the
            right place automatically.
          </p>
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/20">© Vawworld • Phase 1</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <h2 className="font-display font-bold text-3xl tracking-tight">Sign in</h2>
          <p className="mt-2 text-sm text-white/50 font-light">
            Use your Vawworld credentials. New here?{" "}
            <Link to="/signup" className="text-white underline decoration-white/30 underline-offset-4 hover:decoration-white">
              Read this first
            </Link>
            .
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[11px] uppercase tracking-[0.3em] text-white/50">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent border-white/15 focus-visible:border-white text-white rounded-none h-11"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[11px] uppercase tracking-[0.3em] text-white/50">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-transparent border-white/15 focus-visible:border-white text-white rounded-none h-11"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3 rounded-none" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 rounded-none bg-white text-black hover:bg-amber-300 uppercase tracking-[0.25em] text-xs"
              data-testid="login-submit-button"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
